import { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatApiError, getInitials } from '../../lib/utils';
import { USER_ROLES, USER_STATUSES, DESIGNATIONS, formatRoles } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, DotsThree, Pencil, Trash, UserCircle, ShieldCheck } from '@phosphor-icons/react';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const { isAdmin, user: currentUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    designation: '',
    parent_id: '',
    roles: [],
    status: 'active'
  });

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleRole = (roleValue) => {
    setFormData(prev => {
      const current = prev.roles || [];
      const updated = current.includes(roleValue)
        ? current.filter(r => r !== roleValue)
        : [...current, roleValue];
      return { ...prev, roles: updated };
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      mobile: '',
      designation: '',
      parent_id: '',
      roles: [],
      status: 'active'
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.roles.length) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      const res = await createUser(formData);
      setUsers([...users, res.data]);
      setShowCreateModal(false);
      resetForm();
      toast.success('User created. A temporary password has been sent to their email.');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.roles.length) {
      toast.error('Please select at least one role');
      return;
    }
    try {
      await updateUser(editUser.id, formData);
      setUsers(users.map(u => u.id === editUser.id ? { ...u, ...formData } : u));
      setEditUser(null);
      resetForm();
      toast.success('User updated successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteUser(deleteId);
      setUsers(users.filter(u => u.id !== deleteId));
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleteId(null);
    }
  };

  const openEditModal = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      mobile: user.mobile || '',
      designation: user.designation || '',
      parent_id: user.parent_id || '',
      roles: user.roles || (user.role ? [user.role] : []),
      status: user.status
    });
    setEditUser(user);
  };

  const usersMap = {};
  users.forEach(u => { usersMap[u.id] = u.name; });

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <ShieldCheck size={48} className="mx-auto text-zinc-300 mb-4" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-zinc-500 mt-2">Only administrators can manage users.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            User Management
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage team members and their access
          </p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="create-user-button">
              <Plus size={18} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="rounded-sm"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="rounded-sm"
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Mobile</Label>
                  <Input
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Designation</Label>
                  <SearchableSelect
                    value={formData.designation || ''}
                    onValueChange={(v) => handleChange('designation', v)}
                    options={DESIGNATIONS}
                    placeholder="Select designation"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Roles *</Label>
                  <div className="space-y-2 pt-1">
                    {USER_ROLES.map(r => (
                      <div key={r.value} className="flex items-center gap-2">
                        <Checkbox
                          id={`create-role-${r.value}`}
                          checked={formData.roles.includes(r.value)}
                          onCheckedChange={() => toggleRole(r.value)}
                          data-testid={`create-role-${r.value}`}
                        />
                        <label htmlFor={`create-role-${r.value}`} className="text-sm cursor-pointer">{r.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase">Reports To</Label>
                  <SearchableSelect
                    value={formData.parent_id || ''}
                    onValueChange={(v) => handleChange('parent_id', v)}
                    options={[{ value: '', label: 'None (Root)' }, ...users.filter(u => u.id !== editUser?.id).map(u => ({ value: u.id, label: u.name }))]}
                    placeholder="Select manager"
                    searchPlaceholder="Search users..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#002FA7] text-white" data-testid="submit-user-button">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <UserCircle size={48} className="mx-auto text-zinc-300 mb-4" />
            <p className="text-zinc-500">No users found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">User</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Roles</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Reports To</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="hover:bg-zinc-50" data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-zinc-100 text-xs">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{user.name}</p>
                        {user.designation && (
                          <p className="text-xs text-zinc-500">{user.designation}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user.roles || (user.role ? [user.role] : [])).map(r => (
                        <Badge key={r} variant={r === 'admin' ? 'default' : 'secondary'} className="text-xs capitalize">
                          {r === 'account_manager' ? 'AM' : r === 'operations_manager' ? 'OM' : r}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{usersMap[user.parent_id] || '-'}</TableCell>
                  <TableCell>
                    <Badge className={`text-xs ${user.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.id !== currentUser?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <DotsThree size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(user)}>
                            <Pencil size={16} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteId(user.id)} className="text-red-600">
                            <Trash size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={!!editUser} onOpenChange={() => { setEditUser(null); resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Mobile</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => handleChange('mobile', e.target.value)}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Designation</Label>
                <SearchableSelect
                  value={formData.designation || ''}
                  onValueChange={(v) => handleChange('designation', v)}
                  options={DESIGNATIONS}
                  placeholder="Select designation"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Roles *</Label>
                <div className="space-y-2 pt-1">
                  {USER_ROLES.map(r => (
                    <div key={r.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-role-${r.value}`}
                        checked={formData.roles.includes(r.value)}
                        onCheckedChange={() => toggleRole(r.value)}
                        data-testid={`edit-role-${r.value}`}
                      />
                      <label htmlFor={`edit-role-${r.value}`} className="text-sm cursor-pointer">{r.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Status *</Label>
                <SearchableSelect
                  value={formData.status}
                  onValueChange={(v) => handleChange('status', v)}
                  options={USER_STATUSES.map(s => ({ value: s.value, label: s.label }))}
                  placeholder="Select status"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase">Reports To</Label>
                <SearchableSelect
                  value={formData.parent_id || ''}
                  onValueChange={(v) => handleChange('parent_id', v)}
                  options={[{ value: '', label: 'None (Root)' }, ...users.filter(u => u.id !== editUser?.id).map(u => ({ value: u.id, label: u.name }))]}
                  placeholder="Select manager"
                  searchPlaceholder="Search users..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => { setEditUser(null); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#002FA7] text-white">
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this user? Their data will be reassigned to their manager.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
